import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import * as styles from './OptionPlanIndex.module.scss'

import {
  type OptionItem as ApiOptionItem,
  type OptionResponse,
  OptionResponseUsageEnum,
} from '../../../apis/fvillage/api'
import {
  DEFAULT_TRANSACTION_ID,
  PATH,
  REGISTRATION_PATH,
} from '../../../constants'
import {
  useCreateNextPath,
  useLogin,
  useOptions,
  useOptionsApi,
  useSearchQueryParams,
} from '../../../hooks'
import i18n from '../../../i18n'
import { OptionItem, createOptionItemSchema } from '../../../schemas'
import { toFormOptionAnswers } from '../../../utils'
import { Button } from '../../basics'
import { Loader } from '../../contents'
import { MultiColumn } from '../../layouts'
import { LoginModal } from '../../modals'
import { OptionPlanItem } from '../OptionPlanItem'

const translateNamespace = 'components.OptionPlanIndex'

// The night a GameTicket option belongs to. Shared by submit and restore so the
// date stored in sessionStorage is the same key used to match it on return.
// Entries whose stock row carries no date fall back to the check-in date.
const getGameTicketDate = (
  option: OptionResponse | undefined,
  checkInDate: string | undefined
) => option?.stocks?.[0]?.date ?? checkInDate ?? null

export const OptionPlanIndex: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { onCreateNextPath, params } = useCreateNextPath()
  const { params: queryParams } = useSearchQueryParams()
  const { onGet: onGetOptions, onSet: onSetOptions } = useOptions()
  const { data, isLoading } = useOptionsApi({
    property: params.property,
    room: params.room,
    plan: params.plan,
    checkInDate: queryParams.checkInDate,
    checkOutDate: queryParams.checkOutDate,
    numberOfAdults: queryParams.numberOfAdults,
    numberOfChildren: queryParams.numberOfChildren,
    numberOfInfants: queryParams.numberOfInfants,
    roomCount: queryParams.roomCount,
  })
  const [isOpenLoginModal, setLoginModal] = useState(false)
  const { validateToken } = useLogin()

  const guestCount =
    (queryParams.numberOfAdults ?? 0) +
    (queryParams.numberOfChildren ?? 0) +
    (queryParams.numberOfInfants ?? 0)

  // Group GameTicket option indices by the night they belong to. A GameTicket
  // option carries its night in `stocks[0].date` (one option entry per night);
  // entries without a date fall into a single shared bucket so they behave like
  // the previous whole-stay total. The purchase cap is per-night, not across the
  // whole stay, so each night can hold up to `guestCount` tickets.
  const gameTicketIndexGroups = useMemo(() => {
    const groups = new Map<string, number[]>()
    data?.forEach((option, index) => {
      if (option?.usage !== OptionResponseUsageEnum.GameTicket) {
        return
      }
      const dateKey = option?.stocks?.[0]?.date ?? ''
      const indices = groups.get(dateKey) ?? []
      indices.push(index)
      groups.set(dateKey, indices)
    })
    return Array.from(groups.values())
  }, [data])

  const schema = useMemo(
    () =>
      createOptionItemSchema(data ?? []).superRefine((val, ctx) => {
        const exceedsGuests = gameTicketIndexGroups.some((indices) => {
          const ticketsForDate = indices.reduce(
            (sum, idx) => sum + (Number(val.items[idx]?.count) || 0),
            0
          )
          return ticketsForDate > guestCount
        })
        if (exceedsGuests) {
          ctx.addIssue({
            code: 'custom',
            message: i18n.t('validations.gameTicketExceedsGuests'),
            path: ['items'],
          })
        }
      }),
    [data, gameTicketIndexGroups, guestCount]
  )

  const initialValues = useMemo(
    () =>
      data?.map((option) => {
        if (option?.usage === OptionResponseUsageEnum.GameTicket) {
          const stock = option?.stocks?.[0]?.availableStock ?? 0
          const count = Math.min(stock, guestCount)
          return {
            option: option?.id?.toString() ?? '',
            count: count.toString(),
            answers: toFormOptionAnswers(option?.inputFields),
          }
        }
        return {
          option: option?.id?.toString() ?? '',
          count: '0',
          answers: toFormOptionAnswers(option?.inputFields),
        }
      }),
    [data, guestCount]
  )

  const formMethods = useForm<OptionItem>({
    defaultValues: { items: initialValues },
    resolver: zodResolver(schema),
  })

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = formMethods
  const isError = Object.keys(errors).length > 0

  const { fields } = useFieldArray({
    name: 'items',
    control,
  })

  useEffect(() => {
    const options = onGetOptions()
    if (Array.isArray(options) && options.length > 0 && data) {
      const items = data.map((option, index) => {
        const date =
          option?.usage === OptionResponseUsageEnum.GameTicket
            ? getGameTicketDate(option, queryParams.checkInDate)
            : null
        const saved = options.find(
          (item) =>
            item?.option?.toString() === option?.id?.toString() &&
            (item?.date ?? null) === date
        )
        if (!saved) {
          return initialValues?.[index]
        }
        return {
          option: option?.id?.toString() ?? '',
          count: saved.count?.toString() ?? '0',
          answers: toFormOptionAnswers(option?.inputFields, saved.answers),
        }
      })
      reset({ items })
      return
    }
    reset({ items: initialValues })
  }, [data, initialValues, reset, onGetOptions, queryParams.checkInDate])

  const nextPath = onCreateNextPath(REGISTRATION_PATH.registrations, {
    queryParams: { transaction: DEFAULT_TRANSACTION_ID },
    pathParams: { plan: params.plan },
  })

  const onSubmit = handleSubmit(async (formValues) => {
    const options = formValues.items.map((value, index) => {
      const option: ApiOptionItem = {
        option: Number(value.option),
        count: Number(value.count),
      }
      if (option.count) {
        // One answer set per option; unselected options need no answers.
        option.answers = value.answers ?? []
      }
      if (data?.[index]?.usage === OptionResponseUsageEnum.GameTicket) {
        // Send the night this GameTicket actually belongs to so per-night stock
        // is decremented on the right date.
        option.date = getGameTicketDate(data?.[index], queryParams.checkInDate)
      }
      return option
    })
    onSetOptions(options)

    const validToken = await validateToken()
    if (!validToken) {
      setLoginModal(true)
      return
    }

    navigate(nextPath)
  })

  if (isLoading) {
    return <Loader />
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit} className={styles.options}>
        {data &&
          fields
            .filter((field, index) => !!data[index])
            .map((field, index) => {
              const option = data[index]
              return (
                <OptionPlanItem
                  key={`option-plan-item-${option.id}`}
                  option={option}
                  index={index}
                />
              )
            })}
        {(errors.items?.root?.message || errors.items?.message) && (
          <p className={styles.validationError}>
            {errors.items?.root?.message ?? errors.items?.message}
          </p>
        )}
        <MultiColumn isSpColumnReverse>
          <Button
            htmlType="button"
            variant="tertiary"
            size="xmedium"
            outline
            onClick={() => {
              // Plan-first flow: return to the fixed-plan room page rather than
              // the public plan list. Branch on the `from_plan_rooms` query
              // param (not the plan's `isSecret`, nor router state) so the
              // marker survives login redirects / reloads and so a *non-secret*
              // plan opened via a plan-first URL also returns to its room list
              // — matching `RegistrationNewFrame`'s Back (3360001710).
              const backPath = queryParams.fromPlanRooms
                ? PATH.plan.rooms
                : PATH.plans
              navigate(onCreateNextPath(backPath))
            }}
          >
            {queryParams.fromPlanRooms
              ? t(`${translateNamespace}.backToRooms`)
              : t(`${translateNamespace}.back`)}
          </Button>

          <Button
            htmlType="submit"
            variant="primary"
            size="xmedium"
            disabled={isError}
          >
            {t(`${translateNamespace}.next`)}
          </Button>
          <LoginModal
            isOpen={isOpenLoginModal}
            onClose={() => setLoginModal(false)}
            nextPath={nextPath.pathname}
          />
        </MultiColumn>
      </form>
    </FormProvider>
  )
}
